import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  isDashboardRecord,
  type DashboardDefinition,
  type DashboardRecord,
} from "@/shared/domain/dashboards";
import { useUpdateDashboard } from "@/shared/domain/dashboards/hooks";
import {
  RobotoDashboardRevisionConflictException,
  RobotoDomainException,
} from "@/shared/http";

/** Matches the app's error-toast duration for dialog-less mutation failures. */
export const ERROR_NOTIFICATION_DURATION_MS = 6000;

/**
 * String message for `showNotification`, which only accepts strings. A backend
 * 4XX message is server-authored and safe to show verbatim; anything else gets
 * the operation-specific fallback.
 */
export function toastErrorMessage(error: unknown, fallback: string): string {
  return error instanceof RobotoDomainException && error.isClientError()
    ? error.message
    : fallback;
}

export interface SaveDashboardDefinitionOptions {
  /** The save landed; the record is the server's, with its new revision. */
  onSaved: (record: DashboardRecord) => void;
  /** Someone else replaced the definition first. The server's current record
   *  comes with the rejection, so the caller can offer a resolution. */
  onRevisionConflict: (serverRecord: DashboardRecord) => void;
}

export interface SaveDashboardDefinitionArgs {
  dashboardId: string;
  definition: DashboardDefinition;
  /** The revision the canvas was seeded from. Never read from a query cache,
   *  which can drift to a concurrent writer's revision and defeat the check. */
  baseRevision: number;
}

/**
 * Persist a dashboard's definition, with the optimistic-concurrency handshake
 * and the failure surfaces that go with it.
 *
 * One implementation for every surface that saves — the header's Save row and
 * the widget editor's CTA — because a definition write is wholesale: two
 * implementations would be two chances to send one without a `base_revision`,
 * or to swallow a conflict that has a resolution the user should be offered.
 *
 * A conflict earns a dialog (the caller's `onRevisionConflict`) rather than a
 * six-second toast, because it is resolvable. Everything else surfaces as an
 * error toast, the app's pattern for dialog-less mutation failures.
 */
export function useSaveDashboardDefinition({
  onSaved,
  onRevisionConflict,
}: SaveDashboardDefinitionOptions) {
  const showNotification = useNotification();
  const mutation = useUpdateDashboard();

  const save = (
    { dashboardId, definition, baseRevision }: SaveDashboardDefinitionArgs,
    /** Follow-up for this call only — e.g. leaving a surface once its save
     *  has actually landed. */
    onSuccess?: (record: DashboardRecord) => void,
  ) => {
    mutation.mutate(
      {
        dashboardId,
        updates: {
          dashboard_definition: definition,
          base_revision: baseRevision,
        },
      },
      {
        onSuccess: (record) => {
          onSaved(record);
          onSuccess?.(record);
        },
        onError: (error) => {
          if (error instanceof RobotoDashboardRevisionConflictException) {
            const serverRecord = error.currentDashboard;
            if (isDashboardRecord(serverRecord)) {
              onRevisionConflict(serverRecord);
              return;
            }
            // A malformed payload can't drive the dialog; fall through to the
            // ordinary failure surface rather than rendering an empty one.
          }
          showNotification(
            toastErrorMessage(
              error,
              "Failed to save dashboard. Please try again.",
            ),
            "error",
            ERROR_NOTIFICATION_DURATION_MS,
          );
        },
      },
    );
  };

  return { save, isSaving: mutation.isPending };
}
